// src/pages/Team.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Space, Tag, Typography, message, Segmented, Card, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import StandardPage from '../components/Layout/StandardPage';
import ModifyUser from '../components/users/ModifyUser';
import { getTeam, type UserDTO } from '../services/users';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { Search } = Input;

/** Alias leggibili lato UI (USER → STAFF, PJM gestito) */
const roleAlias = (role?: UserDTO['role']) => {
  const r = String(role || '').toUpperCase();
  switch (r) {
    case 'ADMIN': return 'ADMIN';
    case 'PJM':
    case 'PROJECT MANAGER':
    case 'PROJECT_MANAGER':
    case 'MANAGER': return 'PJM';
    case 'USER': return 'STAFF';         // 👈 USER -> STAFF in UI
    case 'FINANCE': return 'FINANCE';
    case 'VIDEOMAKER': return 'VIDEOMAKER';
    case 'CUSTOMER': return 'CUSTOMER';
    default: return r || '—';
  }
};

const roleColor = (alias: string) => {
  switch (alias) {
    case 'ADMIN': return 'volcano';
    case 'PJM': return 'geekblue';
    case 'STAFF': return 'purple';
    case 'FINANCE': return 'gold';
    case 'VIDEOMAKER': return 'blue';
    case 'CUSTOMER': return 'green';
    default: return 'default';
  }
};

type RoleView = 'STAFF' | 'CUSTOMER';

const Team: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<UserDTO[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [selected, setSelected] = useState<UserDTO | null>(null);

  // Solo due viste: STAFF (tutti tranne CUSTOMER) / CUSTOMER
  const [roleView, setRoleView] = useState<RoleView>('STAFF');

  // Ricerca SOLO per la vista CLIENTI
  const [customerSearch, setCustomerSearch] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const teamData = await getTeam();
      setRows(Array.isArray(teamData) ? teamData : []);
    } catch (e: any) {
      message.error(e?.message || 'Impossibile caricare dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const displayName = (u: UserDTO) =>
    `${u.firstName || ''} ${u.lastName || ''}`.trim();

  // Dataset separati
  const dataStaff = useMemo(
    () => rows.filter(u => roleAlias(u.role) !== 'CUSTOMER'),
    [rows]
  );

  const dataCustomerBase = useMemo(
    () => rows.filter(u => roleAlias(u.role) === 'CUSTOMER'),
    [rows]
  );

  // Filtro ricerca per vista CLIENTI
  const dataCustomer = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return dataCustomerBase;
    return dataCustomerBase.filter(u => {
      const name = displayName(u).toLowerCase();
      const email = (u.email || '').toLowerCase();
      const cust = (u as any).customerName ? String((u as any).customerName).toLowerCase() : '';
      return (
        name.includes(q) ||
        email.includes(q) ||
        cust.includes(q)
      );
    });
  }, [dataCustomerBase, customerSearch]);

  // Colonne comuni (nome, email, ultimo accesso, azioni) — RIMOSSA colonna STATO
  const baseCols: ColumnsType<UserDTO> = useMemo(() => [
    {
      title: 'Nome',
      key: 'name',
      sorter: (a, b) => displayName(a).localeCompare(displayName(b), 'it', { sensitivity: 'base' }),
      render: (_: any, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{displayName(r) || '—'}</Text>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email || '').localeCompare(b.email || '', 'it', { sensitivity: 'base' }),
      render: (v?: string) => v || '—',
    },
    {
      title: 'Ultimo accesso',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      sorter: (a, b) => dayjs(a.lastLogin || 0).valueOf() - dayjs(b.lastLogin || 0).valueOf(),
      render: (v?: string) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      title: 'Azioni',
      key: 'action',
      render: (_: any, r) => (
        <Space>
          <Button
            onClick={() => {
              setMode('edit');
              setSelected(r);
              setDrawerOpen(true);
            }}
          >
            Modifica
          </Button>
        </Space>
      ),
    },
  ], [rows]); // ridisegno quando arrivano i dati (per i sorter che leggono dati)

  // Colonne per STAFF (include RUOLO)
  const columnsStaff: ColumnsType<UserDTO> = useMemo(() => {
    const roleCol: ColumnsType<UserDTO>[number] = {
      title: 'Ruolo',
      dataIndex: 'role',
      key: 'role',
      sorter: (a, b) => roleAlias(a.role).localeCompare(roleAlias(b.role), 'it', { sensitivity: 'base' }),
      render: (backendRole: UserDTO['role']) => {
        const alias = roleAlias(backendRole);
        return <Tag color={roleColor(alias)}>{alias}</Tag>;
      },
    };
    const [nameCol, emailCol, lastLoginCol, actionCol] = baseCols;
    return [nameCol, emailCol, roleCol, lastLoginCol, actionCol];
  }, [baseCols]);

  // Colonne per CUSTOMER (usa direttamente customerName dal payload utenti)
  const columnsCustomer: ColumnsType<UserDTO> = useMemo(() => {
    const customerCol: ColumnsType<UserDTO>[number] = {
      title: 'Cliente',
      key: 'customer',
      sorter: (a, b) => {
        const ca = ((a as any).customerName || '') as string;
        const cb = ((b as any).customerName || '') as string;
        return ca.localeCompare(cb, 'it', { sensitivity: 'base' });
      },
      render: (_: any, r) => {
        const name = (r as any).customerName as string | undefined;
        return name ? <Text>{name}</Text> : <Text type="secondary">—</Text>;
      },
    };
    const [nameCol, emailCol, lastLoginCol, actionCol] = baseCols;
    return [nameCol, emailCol, customerCol, lastLoginCol, actionCol];
  }, [baseCols]);

  return (
    <StandardPage
      title="Team"
      subtitle="Gestisci gli utenti della tua azienda"
      actions={[
        {
          label: 'Nuovo utente staff',
          type: 'primary',
          icon: <PlusOutlined />,
          onClick: () => { setMode('create'); setSelected(null); setDrawerOpen(true); },
        },
        { label: 'Ricarica', onClick: fetchData, icon: <ReloadOutlined /> },
      ]}
      loading={loading && rows.length === 0}
      className="page-team"
    >
      {/* Switch vista: Staff / Clienti + barra ricerca SOLO per Clienti */}
      <div style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Segmented<RoleView>
          value={roleView}
          onChange={(val) => setRoleView(val as RoleView)}
          options={[
            { label: 'Staff', value: 'STAFF' },
            { label: 'Clienti', value: 'CUSTOMER' },
          ]}
        />
        <span style={{ color: '#666' }}>
          {(roleView === 'STAFF' ? dataStaff.length : dataCustomer.length)} / {rows.length} utenti
        </span>

        {roleView === 'CUSTOMER' && (
          <Search
            placeholder="Cerca cliente (nome cliente, email, nome/cognome)…"
            allowClear
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            style={{ minWidth: 280, maxWidth: 420 }}
          />
        )}
      </div>

      {/* === STAFF TABLE === */}
      {roleView === 'STAFF' && (
        <Card
          style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            rowKey="id"
            loading={loading}
            columns={columnsStaff}
            dataSource={dataStaff}
            pagination={{
              style: { marginRight: 16 },
              defaultPageSize: 8,
              pageSizeOptions: [8, 10, 20, 50, 100],
              showSizeChanger: true,
              showQuickJumper: true,
            }}
            scroll={{ x: 900 }}
          />
        </Card>
      )}

      {/* === CUSTOMER TABLE === */}
      {roleView === 'CUSTOMER' && (
        <Card
          style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginTop: 12 }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            rowKey="id"
            loading={loading}
            columns={columnsCustomer}
            dataSource={dataCustomer}
            pagination={{
              style: { marginRight: 16 },
              defaultPageSize: 8,
              pageSizeOptions: [8, 10, 20, 50, 100],
              showSizeChanger: true,
              showQuickJumper: true,
            }}
            scroll={{ x: 900 }}
          />
        </Card>
      )}

      <ModifyUser
        open={drawerOpen}
        mode={mode}
        user={selected}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => { setDrawerOpen(false); fetchData(); }}
      />
    </StandardPage>
  );
};

export default Team;
